import { getPremiumContext } from "@memorang/helpers/bundle";
import type { ExamDetails } from "@memorang/types";
import Icon from "@react-native-vector-icons/material-design-icons/static";
import { Image } from "expo-image";
import { SectionList } from "react-native";
import { Divider, Text, TouchableRipple } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import Button from "../Button";
import EmptyState from "../EmptyView";
import ProductListItem from "../ProductListItem";

export type Section = {
	title: string;
	data: ExamDetails[];
	icon: string;
};

type Props = {
	handleOpenProductModal: (bundleId: string, bundleType: string) => void;
	products: Section[];
	handleSwitchExamContext: (context: ExamDetails) => void;
	handleClearSearch?: () => void;
	showProducts?: boolean;
	showChevronRight?: boolean;
	stickySectionHeadersEnabled?: boolean;
	searchQuery?: string;
	currentExamId?: string;
};

const ProductsList = ({
	handleOpenProductModal,
	products,
	handleSwitchExamContext,
	handleClearSearch,
	showProducts = true,
	showChevronRight,
	stickySectionHeadersEnabled = true,
	searchQuery,
	currentExamId,
}: Props) => {
	const theme = useAppTheme();

	const renderSectionHeader = ({
		section: { title, icon },
	}: {
		section: Section;
	}) => {
		if (products.length === 1) {
			return <Box marginTop={16} />;
		}
		return (
			<Box padding={16} gap={16}>
				<Box flexDirection="row" alignItems="center" gap={1}>
					{icon && (
						<Image
							source={{
								uri: icon,
							}}
							style={{
								width: 32,
								height: 32,
								marginRight: 8,
								borderRadius: 4,
							}}
						/>
					)}
					<Text variant="titleMedium">{title}</Text>
				</Box>
				<Divider />
			</Box>
		);
	};

	const renderItem = ({ item: examItem }: { item: ExamDetails }) => {
		const {
			exam: { name: subtitle, bundles: examBundles, id },
		} = examItem;

		const isCurrentExamContext = currentExamId
			? id === String(currentExamId) ||
				examItem.exam.examId === Number(currentExamId) ||
				examItem.exam.id === String(currentExamId)
			: false;

		return (
			<Box paddingBottom={16}>
				<TouchableRipple
					onPress={async () => {
						if (!isCurrentExamContext) {
							await handleSwitchExamContext(examItem);
						}
					}}
					disabled={isCurrentExamContext}
				>
					<Box
						flexDirection="row"
						alignItems="center"
						justifyContent="space-between"
						paddingHorizontal={16}
					>
						<Text
							style={{
								flex: 1,
							}}
							variant="titleSmall"
						>
							{subtitle}
						</Text>
						{showChevronRight ? (
							<Icon
								name="chevron-right"
								color={theme.colors.text}
								size={24}
								style={{
									paddingVertical: 8,
								}}
								onPress={async () => {
									if (!isCurrentExamContext) {
										await handleSwitchExamContext(examItem);
									}
								}}
							/>
						) : (
							<>
								{isCurrentExamContext ? (
									<Button icon="check" disabled>
										Current Exam
									</Button>
								) : (
									<Button
										compact
										mode="text"
										onPress={async () => {
											await handleSwitchExamContext(examItem);
										}}
										icon="arrow-right-bottom"
									>
										Switch to Exam
									</Button>
								)}
							</>
						)}
					</Box>
				</TouchableRipple>
				{showProducts &&
					examBundles &&
					examBundles.map((productItem) => {
						const { subscriptionExpired, subscriptionExpirationDate } =
							getPremiumContext(productItem.metadata);
						const expirationDateText = subscriptionExpirationDate
							? `Expires ${subscriptionExpirationDate}`
							: "";
						const bundleStatusText = subscriptionExpired
							? "Expired"
							: expirationDateText;

						return (
							<Box key={productItem.bundle.id}>
								<ProductListItem
									product={{
										name: productItem.bundle.name,
										iconUrl: productItem.bundle.icon?.url,
										contentsItemsCount: productItem.bundle.contentsItemsCount,
									}}
									size={64}
									statusText={bundleStatusText}
									expired={subscriptionExpired as boolean}
									showRightView
									handleOpenProductModal={() => {
										handleOpenProductModal(
											productItem.bundle.id,
											productItem.bundle.bundleType,
										);
									}}
								/>
							</Box>
						);
					})}
			</Box>
		);
	};

	if (!products.length) {
		return (
			<Box flexGrow={1} height={"100%"}>
				<EmptyState
					title={`No exams found for search term "${searchQuery}"`}
					subtitle="Try a different search term"
					cta="Clear Search"
					handleAction={handleClearSearch}
				/>
			</Box>
		);
	}

	return (
		<SectionList
			sections={products}
			contentContainerStyle={{
				flexGrow: 1,
				paddingBottom: 16,
			}}
			showsVerticalScrollIndicator={false}
			keyExtractor={(item) => item.id}
			renderItem={renderItem}
			stickySectionHeadersEnabled={stickySectionHeadersEnabled}
			renderSectionHeader={renderSectionHeader}
		/>
	);
};

export { ProductsList };
